/**
 * The one "Delete NCR" confirmation, shared by the register's row menu and the
 * NCR page (QUALITY-NCR-REVIEW decision 2b, Aaron 25 Sep 2026: reuse Helix's
 * existing delete rather than add a second meaning of the word).
 *
 * Deleting removes the NCR for good; the server allows it for customer admins
 * (and Helix) on manually raised NCRs only.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { qualityAPI, NonConformanceReport } from '../../lib/api';
import { getErrorMessage } from '../../hooks/useFetchData';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';

export interface NCRDeleteDialogProps {
  ncr: NonConformanceReport;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the NCR is gone (refresh the list, or leave the page). */
  onDeleted: () => void;
}

/**
 * Confirm-then-delete dialog for one NCR.
 *
 * @param props - {@link NCRDeleteDialogProps}
 * @returns the dialog
 */
export default function NCRDeleteDialog({ ncr, open, onOpenChange, onDeleted }: NCRDeleteDialogProps) {
  const [busy, setBusy] = useState(false);
  const label = ncr.ncr_number || `NCR ${ncr.id}`;

  const confirm = async () => {
    setBusy(true);
    try {
      await qualityAPI.deleteNCR(ncr.id);
      toast.success(`${label} deleted`);
      onOpenChange(false);
      onDeleted();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes the NCR and its corrective actions,
            documents and history. It cannot be undone. To keep the record but
            take it off the register, archive it instead.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700"
            disabled={busy}
            onClick={(e) => { e.preventDefault(); confirm(); }}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete NCR
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
